/* istanbul ignore file */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpBaseService } from '../../../core';
import { environment } from '../../../environments/environment';
import { App, MapContextLayer, MapFieldPoint, MapSiteDataPreview, Project } from '../../models';
import { MapPinColor } from '../../shared/enums';
import { ContextLayersUtilityService } from '../utility-services';

@Injectable({
    providedIn: 'root'
})

export class MapDataService extends HttpBaseService {
    private route = environment.api + 'apps/';

    constructor(
        private _httpClient: HttpClient,
        private _contextLayersUtilityService: ContextLayersUtilityService
    ) {
        super(_httpClient);
    }

    getEvaluatedMapPinColorsBySite(app: App, project: Project): Observable<Record<string, MapPinColor>> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/sites/evaluated-map-pin-colors`;
        return this.httpGet(url);
    }

    getEvaluatedMapPinColorsBySiteId(app: App, project: Project, siteId: string): Observable<Record<string, MapPinColor>> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/sites/${siteId}/evaluated-map-pin-colors`;
        return this.httpGet(url);
    }

    updateSiteDataPreview(app: App, project: Project, mapSiteDataPreview: MapSiteDataPreview): Observable<MapSiteDataPreview> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/map-site-data-preview/${mapSiteDataPreview.id}`;
        return this.httpPut(url, mapSiteDataPreview);
    }

    createMapSiteDataPreview(app: App, project: Project, mapSiteDataPreview: MapSiteDataPreview): Observable<MapSiteDataPreview> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/map-site-data-preview`;
        return this.httpPost(url, mapSiteDataPreview);
    }

    getMapSiteDataPreviewByProjectId(app: App, project: Project): Observable<MapSiteDataPreview> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/map-site-data-preview`;
        return this.httpGet(url);
    }

    getSummaryProjectMapSiteDataPreview(app: App, project: Project): Observable<MapSiteDataPreview> {
        const url = `${this.route}${app.id}/summary-projects/${project.id}/map/map-site-data-preview`;
        return this.httpGet(url);
    }

    createMapContextLayer(app: App, mapContextLayer: MapContextLayer): Observable<MapContextLayer> {
        const url = `${this.route}${app.id}/map/map-context-layers`;
        return this.httpPost(url, mapContextLayer).pipe(map((createdLayer) => this._contextLayersUtilityService.castContextLayer(createdLayer)));
    }

    getMapContextLayersForApp(app: App): Observable<MapContextLayer[]> {
        const url = `${this.route}${app.id}/map/map-context-layers`;
        return this.httpGet(url).pipe(map((layers) => this._contextLayersUtilityService.castContextLayers(layers)));
    }

    updateContextLayer(app: App, contextLayerToUpdate: MapContextLayer): Observable<MapContextLayer> {
        const url = `${this.route}${app.id}/map/map-context-layers/${contextLayerToUpdate.id}`;
        return this.httpPut(url, contextLayerToUpdate).pipe(map((updatedLayer) => this._contextLayersUtilityService.castContextLayer(updatedLayer)));
    }

    deleteContextLayer(currentApp: App, layer: MapContextLayer): Observable<MapContextLayer> {
        const url = `${this.route}${currentApp.id}/map/map-context-layers/${layer.id}`;
        return this.httpDelete(url).pipe(map((deletedLayer) => this._contextLayersUtilityService.castContextLayer(deletedLayer)));
    }

    updateContextLayerOrders(app: App, newOrderByContextLayerId: Record<string, number>): Observable<MapContextLayer[]> {
        const url = `${this.route}${app.id}/map/map-context-layers/reorder`;
        return this.httpPut(url, newOrderByContextLayerId).pipe(map((updatedLayers) => this._contextLayersUtilityService.castContextLayers(updatedLayers)));
    }

    createMapFieldPoint(app: App, mapFieldPoint: MapFieldPoint): Observable<MapFieldPoint> {
        const url = `${this.route}${app.id}/projects/${mapFieldPoint.projectId}/map/map-field-points`;
        return this.httpPost(url, mapFieldPoint);
    }

    getMapFieldPointByProject(app: App, project: Project): Observable<MapFieldPoint> {
        const url = `${this.route}${app.id}/projects/${project.id}/map/map-field-points`;
        return this.httpGet(url);
    }

    updateMapFieldPoint(app: App, fieldPointToUpdate: MapFieldPoint): Observable<MapFieldPoint> {
        const url = `${this.route}${app.id}/projects/${fieldPointToUpdate.projectId}/map/map-field-points/${fieldPointToUpdate.id}`;
        return this.httpPut(url, fieldPointToUpdate);
    }
}
